import ConsoleLayout from '@/components/layout/ConsoleLayout';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import PATH_PAGE from '@/router/path';
import { RouteParams } from '@/router/type';
import { StatusGetInstances } from '@/service/one-click-deploy/type';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import BadgeStatus from '../components/BadgeStatus';
import { useEffect, useMemo, useRef } from 'react';
import { useGetDedicateInstanceById } from './hook/useGetDedicateInstanceById';
import DataNotFound from '@/components/ui/not-found/data-not-found';
import LoadingBar, { LoadingBarRef } from 'react-top-loading-bar';
import { Skeleton } from '@/components/ui/skeleton';
import useConsoleLayoutConfig from '@/hook/useConsoleLayoutConfig';

const OneClickDeployDetail = () => {
  const { orgId, workspaceId } = useParamWorkspaceId();
  const { instanceId } = useParams<RouteParams>();
  const navigate = useNavigate();
  const { data, isError, isLoading } = useGetDedicateInstanceById();

  const loadingBarRef = useRef<LoadingBarRef>(null);

  const navItems = [
    {
      href: PATH_PAGE.dashboard.oneClickDeploy?.root(orgId, workspaceId),
      label: 'All Instances',
      separator: true,
      strictMatch: true,
    },
    {
      href: PATH_PAGE.dashboard.oneClickDeploy.byId.root(
        orgId,
        workspaceId,
        instanceId!
      ),
      label: 'Overview',
      strictMatch: true,
    },
    {
      href: PATH_PAGE.dashboard.oneClickDeploy.byId.usageAndCost(
        orgId,
        workspaceId,
        instanceId!
      ),
      label: 'Usage & Cost',
      strictMatch: true,
    },
    {
      label: 'Activity',
      href: PATH_PAGE.dashboard.oneClickDeploy.byId.activity(
        orgId,
        workspaceId,
        instanceId!
      ),
      strictMatch: true,
    },
    {
      href: PATH_PAGE.dashboard.oneClickDeploy.byId.deployments(
        orgId,
        workspaceId,
        instanceId!
      ),
      label: 'Deployments',
      strictMatch: true,
    },
    {
      href: PATH_PAGE.dashboard.oneClickDeploy.byId.setting(
        orgId,
        workspaceId,
        instanceId!
      ),
      label: 'Setting',
      strictMatch: true,
    },
  ];

  const result = {
    instancesName: data?.instance_name,
    instancesStatus: data?.status as StatusGetInstances,
  };

  useEffect(() => {
    loadingBarRef.current?.continuousStart();
  }, []);

  useEffect(() => {
    if (!instanceId) {
      navigate(PATH_PAGE.dashboard.oneClickDeploy?.root(orgId, workspaceId));
    }
  }, [instanceId, orgId, workspaceId, navigate]);

  const navbarConfig = useMemo(
    () => ({
      title: 'One click Deploy',
      navbar: navItems,
    }),
    [workspaceId, instanceId]
  );
  useConsoleLayoutConfig({
    navConfig: navbarConfig,
  });

  if (isLoading) {
    return (
      <>
        <LoadingBar color="#3c92e7" ref={loadingBarRef} />
        <div className="container mt-8">
          <Skeleton className="h-4 w-48" />
          <div className="mt-6 flex flex-col gap-4">
            <Skeleton className="h-4 w-80" />
            <Skeleton className="h-4 w-80" />
          </div>
        </div>
      </>
    );
  }

  if (!data?.instance_id || isError) {
    return (
      <DataNotFound
        goBackHref={PATH_PAGE.dashboard.oneClickDeploy?.root(orgId, workspaceId)}
      />
    );
  }
  return (
    <div className="container mt-8">
      <div className="flex gap-2">
        <p className="text-lg font-semibold">{result.instancesName}</p>
        <BadgeStatus status={result.instancesStatus} />
      </div>
      <div className="mt-3">
        <Outlet />
      </div>
    </div>
  );
};

export default OneClickDeployDetail;
